/**
 * Iter 207 PART A — Equipment Cleaning frontend panel.
 * Consumes Iter206 backend /api/equipment/cleaning. Preview-first PDF.
 * §6 no duplicate event creation from opening panel/preview.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Modal, Btn, Select, Input, Badge, Textarea } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { Droplets, FileText, CheckCircle2, Trash2 } from "lucide-react";
import { todayTR } from "@/lib/date";

export default function EquipmentCleaningPanel({ productionOrderId, prodNo }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ equipment_id: "", cleaned_by_user_id: "", checked_by_user_id: "",
                                        cleaning_date: todayTR(), notes: "" });

  const load = useCallback(async () => {
    try {
      const q = productionOrderId ? `?production_order_id=${productionOrderId}` : "";
      const r = await api.get(`/equipment/cleaning${q}`);
      setRows(Array.isArray(r) ? r : []);
    } catch { setRows([]); }
  }, [productionOrderId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/equipment").then(r => setEquipmentList(r.items || r || [])).catch(() => setEquipmentList([]));
    api.get("/users").then(r => setUsers(Array.isArray(r) ? r : [])).catch(() => setUsers([]));
  }, []);

  const save = async () => {
    if (!form.equipment_id) { alert("Ekipman seçin"); return; }
    try {
      await api.post("/equipment/cleaning", {
        ...form, production_order_id: productionOrderId || null,
        cleaned_by_user_id: form.cleaned_by_user_id || null,
        checked_by_user_id: form.checked_by_user_id || null,
        notes: form.notes || null,
      });
      setOpen(false); setForm({ ...form, equipment_id: "", cleaned_by_user_id: "", checked_by_user_id: "", notes: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const complete = async (id, checkedById) => {
    await api.patch(`/equipment/cleaning/${id}`, { status: "COMPLETED", checked_by_user_id: checkedById || null });
    load();
  };
  const del = async (id) => {
    if (!confirm("Kayıt silinsin mi?")) return;
    await api.delete(`/equipment/cleaning/${id}`); load();
  };
  const dlPdf = async (id) => {
    try {
      const blob = await api.getBlob(`/equipment/cleaning/${id}/pdf`);
      openPdfPreview(blob, `Temizlik_${id.slice(0, 8)}.pdf`);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  return (
    <div className="mb-4" data-testid="equipment-cleaning-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase text-slate-600">Ekipman Temizlik ({rows.length})</div>
        <Btn size="sm" variant="secondary" onClick={() => setOpen(true)} data-testid="ec-open"><Droplets size={12}/> Yeni Temizlik</Btn>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs italic text-slate-400 border border-dashed border-slate-200 rounded p-2">
          Bu üretim için temizlik kaydı yok. "Yeni Temizlik" ile gerçek olay kaydı ekleyin (otomatik oluşturulmaz §11).
        </div>
      ) : (
        <table className="w-full text-xs border border-slate-200 rounded">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="text-left px-2 py-1">Kod</th><th className="text-left px-2 py-1">Ekipman</th>
            <th className="text-left px-2 py-1">Tarih</th><th className="text-left px-2 py-1">Temizleyen</th>
            <th className="text-left px-2 py-1">Kontrol</th><th className="text-left px-2 py-1">Durum</th>
            <th className="text-left px-2 py-1">İşlem</th>
          </tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`ec-row-${r.id}`}>
              <td className="px-2 py-1 font-mono text-[10px]">{r.equipment_code_snapshot}</td>
              <td className="px-2 py-1">{r.equipment_name_snapshot}</td>
              <td className="px-2 py-1 font-mono text-[10px]">{r.cleaning_date}</td>
              <td className="px-2 py-1">{r.cleaned_by_name}</td>
              <td className="px-2 py-1">{r.checked_by_name || "—"}</td>
              <td className="px-2 py-1"><Badge level={r.status === "COMPLETED" ? "ok" : "warn"}>{r.status}</Badge></td>
              <td className="px-2 py-1">
                <div className="flex gap-1">
                  <Btn size="sm" variant="ghost" onClick={() => dlPdf(r.id)} data-testid={`ec-pdf-${r.id}`}><FileText size={10}/></Btn>
                  {r.status !== "COMPLETED" && <Btn size="sm" onClick={() => complete(r.id)} data-testid={`ec-complete-${r.id}`}><CheckCircle2 size={10}/></Btn>}
                  <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`ec-del-${r.id}`}><Trash2 size={10}/></Btn>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Yeni Ekipman Temizlik Kaydı">
        <div className="text-xs text-slate-500 mb-2">Kayıt yapan: <b>{user?.name || user?.username}</b>. §9 temizlik kimyası kaydedilmez.</div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Ekipman *" value={form.equipment_id} onChange={e => setForm({ ...form, equipment_id: e.target.value })} data-testid="ec-eq">
            <option value="">— Seçin —</option>
            {equipmentList.map(e => (<option key={e.id} value={e.id}>{e.code} — {e.name}</option>))}
          </Select>
          <Input type="date" label="Tarih" value={form.cleaning_date} onChange={e => setForm({ ...form, cleaning_date: e.target.value })}/>
          <Select label="Temizleyen (opsiyonel)" value={form.cleaned_by_user_id} onChange={e => setForm({ ...form, cleaned_by_user_id: e.target.value })} data-testid="ec-cleaner">
            <option value="">Ben ({user?.name || user?.username})</option>
            {users.map(u => (<option key={u.id} value={u.id}>{u.name || u.username}</option>))}
          </Select>
          <Select label="Kontrol Eden (opsiyonel)" value={form.checked_by_user_id} onChange={e => setForm({ ...form, checked_by_user_id: e.target.value })} data-testid="ec-checker">
            <option value="">—</option>
            {users.map(u => (<option key={u.id} value={u.id}>{u.name || u.username}</option>))}
          </Select>
        </div>
        <Textarea label="Not" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}/>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="ec-save"><Droplets size={12}/> Kaydet</Btn>
        </div>
      </Modal>
    </div>
  );
}
